import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {DayOfWeek} from '@generated/models'
import {
  PartnerWorkingHour,
  UpsertPartnerWorkingHourRequest,
} from '@common/models'
import {
  formatDay,
  toTimeString,
  withSeconds,
  isValidTimeHHmm,
  isTimeOrderValid,
} from '@common/utils/dates'

import {SideModal} from '~/components/general/modal/SideModal'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Input} from '~/components/general/input/Input'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {Button} from '~/components/general/button/Button'
import {
  useCreatePartnerWorkingHour,
  useUpdatePartnerWorkingHour,
} from '~/api/partnerWorkingHours'

type PartnerWorkingHoursModalProps = {
  isVisible: boolean
  onClose: () => void
  workingHour?: PartnerWorkingHour | null
}

function formatDayLabel(day: DayOfWeek): string {
  return formatDay(day)
}

export const PartnerWorkingHoursModal: React.FC<
  PartnerWorkingHoursModalProps
> = ({isVisible, onClose, workingHour}) => {
  const {t} = useTranslation<string>()

  const isEditing = !!workingHour

  const [selectedDay, setSelectedDay] = useState<DayOfWeek | undefined>(
    workingHour?.dayOfWeek,
  )
  const [startTime, setStartTime] = useState<string>(
    workingHour ? toTimeString(workingHour.startTime) : '',
  )
  const [endTime, setEndTime] = useState<string>(
    workingHour ? toTimeString(workingHour.endTime) : '',
  )

  const {mutate: createWorkingHour, isPending: isCreating} =
    useCreatePartnerWorkingHour()
  const {mutate: updateWorkingHour, isPending: isUpdating} =
    useUpdatePartnerWorkingHour(workingHour?.id || '')

  const dayOptions = useMemo(
    () =>
      Object.values(DayOfWeek).map((value) => ({
        label: formatDayLabel(value),
        value,
      })),
    [],
  )

  const startError = !isValidTimeHHmm(startTime) ? t('Invalid time') : undefined
  const endError = !isValidTimeHHmm(endTime) ? t('Invalid time') : undefined
  const orderingError =
    !startError &&
    !endError &&
    startTime &&
    endTime &&
    !isTimeOrderValid(startTime, endTime)
      ? t('Start time must be before end time')
      : undefined

  const canSave =
    !!selectedDay &&
    !!startTime &&
    !!endTime &&
    !startError &&
    !endError &&
    !orderingError

  // Sync fields when opening in edit mode or when switching the item
  useEffect(() => {
    if (isVisible && workingHour) {
      setSelectedDay(workingHour.dayOfWeek)
      setStartTime(toTimeString(workingHour.startTime))
      setEndTime(toTimeString(workingHour.endTime))
    }
    if (isVisible && !workingHour) {
      setSelectedDay(undefined)
      setStartTime('')
      setEndTime('')
    }
  }, [isVisible, workingHour])

  const handleSave = () => {
    if (!canSave) return

    const payload: UpsertPartnerWorkingHourRequest = {
      dayOfWeek: selectedDay!,
      startTime: withSeconds(startTime),
      endTime: withSeconds(endTime),
    }

    if (isEditing && workingHour) {
      updateWorkingHour(payload, {
        onSuccess: onClose,
      })
    } else {
      createWorkingHour(payload, {
        onSuccess: onClose,
      })
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <div>
          <Typography size="lg" weight="medium">
            {isEditing ? t('Edit working hour') : t('Add working hour')}
          </Typography>
        </div>
      }>
      <Container flex direction="column" spacing={3}>
        <Container flex direction="column" spacing={1}>
          <Typography>{t('Day of week')}</Typography>
          <FilteredSelect
            key={selectedDay ?? 'none'}
            width={300}
            placeholder={t('Select a day')}
            options={dayOptions}
            initialValue={
              selectedDay
                ? dayOptions.find((d) => d.value === selectedDay)?.label
                : ''
            }
            onChange={(value) => setSelectedDay(value as DayOfWeek | undefined)}
          />
        </Container>

        <Container flex spacing={3}>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('Start time')}</Typography>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              errorMessage={startError || orderingError}
            />
          </Container>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('End time')}</Typography>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              errorMessage={endError || orderingError}
            />
          </Container>
        </Container>

        <Container flex justify="flex-end" spacing={2}>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!canSave || isCreating || isUpdating}>
            {t('Save')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}

export default PartnerWorkingHoursModal
